// @ts-strict
import { modelOptions, mongoose, post, prop, type Ref } from "@typegoose/typegoose";
import { AreaOfInterest } from "../areaOfInterest/areaOfInterest.js";
import { DeviceVersion } from "../commonSubSchemas/deviceVersion.js";
import { LoraNetworkProfile } from "../loraNetworkProfile/loraNetworkProfile.js";
import { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";

const SCHEMA_KEY = "DefaultLoraNetworkProfilePerDevice" as const;

type FieldsWithDefaults = never;

/**
 * MongoDB view that represents devices with their automatically-selected default LoRa network profile.
 *
 * This view is based on the "devices" collection and performs a lookup to find the highest-priority
 * LoraNetworkProfile that matches the device based on:
 * - Device version selector
 * - Organization selector (empty or contains device owner)
 * - AOI selector (empty or contains device AOI)
 * - Priority > 0
 *
 * The result includes selected device fields and the matched defaultLoraNetworkProfile.
 */
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: DefaultLoraNetworkProfilePerDevice.schemaKey,
        timestamps: false,
        _id: true,
        autoCreate: false,
    },
})
@post<SchemaBase<DefaultLoraNetworkProfilePerDevice>>(...addSchemaKeyHook(SCHEMA_KEY))
export class DefaultLoraNetworkProfilePerDevice extends SchemaBase<
    DefaultLoraNetworkProfilePerDevice,
    FieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    // Fields projected from Device collection

    @prop({ type: () => mongoose.Types.ObjectId, required: true })
    public _id!: mongoose.Types.ObjectId;

    @prop({ type: () => String, required: true })
    public node_id!: string;

    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "AreaOfInterest" })
    public _aoi?: Ref<AreaOfInterest>;

    @prop({ type: () => DeviceVersion, required: false })
    public version?: DeviceVersion;

    @prop({ type: () => LoraNetworkProfile, required: false })
    public defaultLoraNetworkProfile?: LoraNetworkProfile;

    static getters = {} as const satisfies Record<string, (self: DefaultLoraNetworkProfilePerDevice) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    constructor(properties?: SchemaConstructorArgs<DefaultLoraNetworkProfilePerDevice, FieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}
